import { useState } from "react";
import { Panel, Label } from "@/components/SplitLayout";
import CopyButton from "@/components/CopyButton";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function JsonFormatter() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [indent, setIndent] = useState(2);

  const process = (minify) => {
    if (!input.trim()) {
      setOutput("");
      setError("");
      return;
    }
    try {
      const parsed = JSON.parse(input);
      setOutput(JSON.stringify(parsed, null, minify ? 0 : indent));
      setError("");
    } catch (e) {
      setError(e.message);
      setOutput("");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Input JSON</Label>
          <textarea
            data-testid="json-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='{"hello":"world","items":[1,2,3]}'
            className="h-[360px] w-full resize-none border border-border bg-card p-5 font-mono text-sm outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="json-beautify"
            onClick={() => process(false)}
            className="bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-primary-foreground transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.98]"
          >
            Beautify
          </button>
          <button
            data-testid="json-minify"
            onClick={() => process(true)}
            className="border border-border bg-card px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] transition-all duration-150 hover:-translate-y-0.5 hover:border-primary hover:text-primary"
          >
            Minify
          </button>
          <div className="flex items-center gap-2">
            <Label>Indent</Label>
            <select
              data-testid="json-indent"
              value={indent}
              onChange={(e) => setIndent(+e.target.value)}
              className="border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            >
              <option value={2}>2</option>
              <option value={4}>4</option>
            </select>
          </div>
        </div>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start space-y-4">
        {error ? (
          <div
            data-testid="json-error"
            className="flex items-start gap-3 border border-destructive bg-destructive/10 p-4 text-sm text-destructive"
          >
            <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
            <span className="font-mono">{error}</span>
          </div>
        ) : output ? (
          <div className="flex items-center gap-2 border border-green-600 bg-green-600/10 px-4 py-2.5 text-sm text-green-700 dark:text-green-500">
            <CheckCircle size={16} weight="fill" /> Valid JSON
          </div>
        ) : null}
        <Panel
          title="Output"
          testId="json-output"
          action={output ? <CopyButton text={output} /> : null}
        >
          <pre className="min-h-[300px] overflow-auto whitespace-pre-wrap break-all bg-background p-4 font-mono text-sm leading-relaxed">
            {output || <span className="text-muted-foreground">Formatted JSON appears here…</span>}
          </pre>
        </Panel>
      </div>
    </div>
  );
}
